import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, CreditCard, LoaderCircle, ShieldCheck, Sparkles } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { createMembershipCheckoutSession, getPublicMembershipPlans } from "../services/billing";

type PublicPlan = Awaited<ReturnType<typeof getPublicMembershipPlans>>["data"]["plans"][number];

export default function JoinLockerRoom() {
  const { user, appUser } = useAuth();
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [membershipBusyId, setMembershipBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const plansRes = await getPublicMembershipPlans({});
        if (!alive) return;
        setPlans(plansRes.data.plans);
      } catch (err: any) {
        if (alive) {
          setError(err?.message || "Failed to load membership options.");
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  async function handleMembershipCheckout(planId: string) {
    if (!user) {
      window.location.href = "/signup";
      return;
    }

    try {
      setMembershipBusyId(planId);
      setError(null);
      const result = await createMembershipCheckoutSession({
        membershipPlanId: planId,
        origin: window.location.origin,
      });

      if (result.data.url) {
        window.location.href = result.data.url;
      }
    } catch (err: any) {
      setError(err?.message || "Failed to start membership checkout.");
    } finally {
      setMembershipBusyId(null);
    }
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <section className="rounded-[1.9rem] border border-[color:rgba(136,156,255,0.12)] bg-[radial-gradient(circle_at_top_left,rgba(58,84,168,0.16),transparent_30%),linear-gradient(180deg,rgba(12,14,22,0.98),rgba(8,9,14,0.98))] p-6 sm:p-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-[color:rgba(136,156,255,0.18)] bg-[color:rgba(59,84,170,0.18)] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-[color:#dbe6ff]">
            <Sparkles className="h-3.5 w-3.5" />
            Memberships
          </div>
          <h1 className="mt-4 text-3xl font-heading tracking-tight text-white sm:text-5xl">
            Join Locker Room.
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-white/58 sm:text-base">
            Start a membership for full app access, member booking, and your complete Locker Room experience.
          </p>
          <div className="mt-5 flex flex-wrap gap-3 text-sm text-white/70">
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5">
              Membership unlocks the full app
            </span>
            <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5">
              One-off sessions live at <Link to="/payg" className="text-sky-300 underline">/payg</Link>
            </span>
          </div>
        </section>

        {loading ? (
          <div className="mt-6 rounded-3xl border border-white/10 bg-neutral-950 p-6 text-neutral-400">
            Loading membership options...
          </div>
        ) : (
          <section className="mt-6 rounded-[1.75rem] border border-white/10 bg-neutral-950 p-5 sm:p-6">
            <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.26em] text-neutral-400">
              <ShieldCheck className="h-4 w-4 text-sky-300" />
              Membership plans
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className="rounded-[1.5rem] border border-white/10 bg-white/[0.03] p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xl font-semibold text-white">{plan.name}</div>
                      <div className="mt-1 text-sm uppercase tracking-[0.2em] text-neutral-500">
                        {plan.tierCode} · {plan.interval}
                      </div>
                    </div>
                    <span className="rounded-full border border-sky-500/20 bg-sky-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-sky-200">
                      {plan.appAccess} access
                    </span>
                  </div>

                  <div className="mt-5 space-y-2 text-sm text-neutral-300">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                      Full member login and profile access
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                      {plan.bookingPolicy.canBookClasses
                        ? "Can book member classes"
                        : "No class booking included"}
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-300" />
                      {plan.bookingPolicy.weeklyBookingLimit
                        ? `${plan.bookingPolicy.weeklyBookingLimit} bookings per week`
                        : "No weekly booking cap"}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleMembershipCheckout(plan.id)}
                    disabled={membershipBusyId === plan.id}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full border border-sky-500/25 bg-sky-500/10 px-4 py-3 text-sm font-semibold text-sky-50 transition hover:bg-sky-500/15 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {membershipBusyId === plan.id ? (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4" />
                    )}
                    {user ? "Start membership" : "Create account to join"}
                  </button>
                </div>
              ))}
            </div>

            {!user ? (
              <p className="mt-5 text-sm text-neutral-400">
                Membership checkout needs an account first so Locker Room can attach your
                subscription to your member profile. <Link to="/signup" className="text-sky-300 underline">Create your account</Link>.
              </p>
            ) : appUser?.appAccessStatus === "full" ? (
              <p className="mt-5 text-sm text-emerald-300">
                Your account already has full member access. You can manage billing from your profile.
              </p>
            ) : null}
          </section>
        )}

        {error ? (
          <div className="mt-6 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}
